import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

import { RootState } from '../../../store';
import useInternationalization from '../../../hooks/useInternationalization';
import { isMobileWidthVideoPanel } from '../../../store/screen/panelSelectors';
import { currentTeam, qualityChecks } from '../../../store/translation/selectors';

import fade from '../../../styles/transitions/fade.module.css';
import styles from './styles.module.css';
import { useTextStyle } from '../../../hooks/useTextStyle';

const BIBLE_PROJECT_URL = 'https://www.bibleproject.com';
const JMBBB_TRANSLATION_CODE = 'JMBBB';
const GENESIS_BOOK_CODE = 'GEN';

const qualityCheckText = (checks: string[]): string =>
  qualityChecks.length === 2 ? `${checks[0]} & ${checks[1]}` : checks[0];

const CopyrightBar = (): JSX.Element => {
  const { darkMode } = useSelector((state: RootState) => state.settings);
  const { getIntlText } = useInternationalization();

  const isMobile = useSelector(isMobileWidthVideoPanel);

  const currentQualityChecks = useSelector(qualityChecks);

  const team = useSelector(currentTeam);

  const { code, bookOrSlug } = useSelector((state: RootState) => state.params);
  const showBibleProjectAttribution =
    code?.toUpperCase() === JMBBB_TRANSLATION_CODE &&
    bookOrSlug?.toUpperCase() === GENESIS_BOOK_CODE;

  const key = team?.id ?? 'no-team';

  const imgUrl = darkMode && team?.logo_darkmode_url ? team?.logo_darkmode_url : team?.logo_url;

  const isWebsite =
    team?.website_url !== undefined &&
    team.website_url !== null &&
    team?.website_url.toString().length > 0;

  const openTeamWebsite = useCallback((): void => {
    if (isWebsite) {
      window.open(team.website_url);
    }
  }, [isWebsite, team]);

  const container = `${styles.container} ${darkMode && styles.containerDarkMode} ${
    isMobile && styles.containerMobile
  }`;

  const logoContainer = `${styles.logoContainer} ${darkMode && styles.logoContainerDarkMode}`;

  const textStyle = useTextStyle({ large: '1.5em', normal: '1em', small: '0.9em' }, true);

  return (
    <SwitchTransition mode="out-in">
      <CSSTransition key={key} timeout={300} classNames={fade}>
        <div style={textStyle}>
          <div className={container}>
            <div
              aria-label="Visit Website"
              className={logoContainer}
              role="button"
              onKeyDown={undefined}
              tabIndex={0}
            >
              <img src={imgUrl} alt="" className={styles.logo} crossOrigin="anonymous" />
            </div>
            <div className={styles.textColumn}>
              <div className={styles.textRow}>
                {team?.copyright && (
                  <div
                    style={{
                      textDecoration: isWebsite ? 'underline' : 'none',
                      color: isWebsite ? '#3a90ff' : 'inherit',
                      cursor: isWebsite ? 'pointer' : 'default',
                    }}
                    onClick={openTeamWebsite}
                    className={styles.copyrightText}
                    onKeyDown={undefined}
                    tabIndex={0}
                    role="button"
                  >{`${getIntlText('translated_by')}: ${team?.copyright}`}</div>
                )}
                {currentQualityChecks !== undefined && currentQualityChecks.length > 0 && (
                  <div className={styles.qualityChecks}>
                    {` | Quality-checked by ${qualityCheckText(currentQualityChecks)}`}
                    {/* <VideoTooltip
                    height={400}
                    width={400}
                    id="approvalTooltipBar"
                    src="https://customer-00iafjcyy4sqllqg.cloudflarestream.com/b6dfddcd6483a1a86fb18a4d363b9e17/manifest/video.m3u8"
                  /> */}
                  </div>
                )}
              </div>
              {showBibleProjectAttribution && (
                <div className={styles.bibleProjectAttribution}>
                  Graphics used with permission from BibleProject (
                  <a
                    className={styles.bibleProjectLink}
                    href={BIBLE_PROJECT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    www.bibleproject.com
                  </a>
                  )
                </div>
              )}
            </div>
          </div>
        </div>
      </CSSTransition>
    </SwitchTransition>
  );
};

export default CopyrightBar;
